import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PLACEHOLDER_PARTS } from '@/constants/marketplace.data'
import { MarketplaceBreadcrumb } from '@/components/common/marketplace-breadcrumb'
import { PartCard, ListingHeader } from '@/components/marketplace'
import { EmptyState } from '@/components/feedback'
import {
  Filter,
  RotateCcw,
  Loader2,
  Search,
  Sparkles,
  Car,
  Wrench,
  CheckCircle2,
  HelpCircle,
  PhoneCall,
  SlidersHorizontal,
  ChevronRight,
  ShieldCheck,
  Send,
} from 'lucide-react'
import { useParts, usePartCategories } from '@/modules/parts/hooks/use-parts'
import { CartDrawer } from '@/modules/cart/components/cart-drawer'
import {
  PARTS_CATEGORIES_DATA,
  PART_CONDITIONS,
  POPULAR_VEHICLE_BRANDS,
  VEHICLE_TYPES,
  AVAILABILITY_STATUSES,
  POPULAR_YEARS,
  ENGINE_VARIANTS,
} from '@/constants/parts-taxonomy'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { toast } from 'sonner'

export function AllPartsPage() {
  const [searchParams] = useSearchParams()

  // ── Search & Layout State ────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || searchParams.get('search') || '')
  const [layout, setLayout] = useState<'grid' | 'list'>('grid')
  const [sortBy, setSortBy] = useState('featured')
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false)
  const [isCartOpen, setIsCartOpen] = useState(false)

  // ── Filters State (Matching Client Images) ──────────────────────────────
  const [activeTab, setActiveTab] = useState<'ALL' | 'NEW' | 'USED'>('ALL')
  const [selectedVehicleType, setSelectedVehicleType] = useState('CAR')
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '')
  const [selectedCondition, setSelectedCondition] = useState('ALL')
  const [selectedBrand, setSelectedBrand] = useState(searchParams.get('brand') || '')
  const [selectedModel, setSelectedModel] = useState(searchParams.get('model') || '')
  const [selectedAvailability, setSelectedAvailability] = useState('ALL')
  const [oemOnly, setOemOnly] = useState(false)
  const [minPrice, setMinPrice] = useState('')
  const [maxPrice, setMaxPrice] = useState('')

  // Sync state if URL search parameters change
  useEffect(() => {
    const cat = searchParams.get('category')
    const brand = searchParams.get('brand')
    const model = searchParams.get('model')
    const q = searchParams.get('q') || searchParams.get('search')
    if (cat !== null) setSelectedCategory(cat)
    if (brand !== null) setSelectedBrand(brand)
    if (model !== null) setSelectedModel(model)
    if (q !== null) setSearchQuery(q)
  }, [searchParams])

  // ── "Find My Part" Stepper State ─────────────────────────────────────────
  const [isFinderOpen, setIsFinderOpen] = useState(false)
  const [stepBrand, setStepBrand] = useState('Maruti Suzuki')
  const [stepModel, setStepModel] = useState('Swift')
  const [stepYear, setStepYear] = useState('2019')
  const [stepVariant, setStepVariant] = useState('1.2L Petrol')
  const [stepCategory, setStepCategory] = useState('Brakes')

  // ── "Request a Part" Modal State ─────────────────────────────────────────
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false)
  const [requestData, setRequestData] = useState({
    vehicleBrand: 'Maruti Suzuki',
    vehicleModel: 'Swift',
    year: '2019',
    partName: '',
    partNumber: '',
    phone: '',
    notes: '',
  })
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false)

  // ── Queries ──────────────────────────────────────────────────────────────
  const { data: dbData, isLoading } = useParts({
    search: searchQuery || undefined,
    category: selectedCategory || undefined,
    isOEM: oemOnly ? true : undefined,
    limit: 100,
  })

  // Dynamic models for selected brand in filter
  const brandModels = useMemo(() => {
    if (!selectedBrand) return []
    const found = POPULAR_VEHICLE_BRANDS.find((b) => b.brand.toLowerCase() === selectedBrand.toLowerCase())
    return found ? found.models : []
  }, [selectedBrand])

  // Dynamic models for stepper
  const stepBrandModels = useMemo(() => {
    const found = POPULAR_VEHICLE_BRANDS.find((b) => b.brand.toLowerCase() === stepBrand.toLowerCase())
    return found ? found.models : ['Standard Model']
  }, [stepBrand])

  // Combine DB parts and placeholder fallback
  const partsList = useMemo(() => {
    const baseList = dbData?.items && dbData.items.length > 0 ? dbData.items : PLACEHOLDER_PARTS

    return baseList.filter((part: any) => {
      // 1. Search Query
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        const matchTitle = part.title?.toLowerCase().includes(q)
        const matchPartNo = part.partNumber?.toLowerCase().includes(q)
        const matchBrand = part.brand?.toLowerCase().includes(q)
        const matchCategory = part.category?.toLowerCase().includes(q)
        if (!matchTitle && !matchPartNo && !matchBrand && !matchCategory) return false
      }

      // 2. Tab Filter (New Parts vs Used Parts)
      if (activeTab === 'NEW') {
        const cond = (part.condition || 'NEW').toUpperCase()
        if (!cond.includes('NEW')) return false
      }
      if (activeTab === 'USED') {
        const cond = (part.condition || '').toUpperCase()
        if (!cond.includes('USED') && !cond.includes('PRE-OWNED')) return false
      }

      // 3. Condition Filter (if specified explicitly)
      if (selectedCondition !== 'ALL') {
        const cond = (part.condition || 'NEW').toUpperCase()
        if (selectedCondition === 'NEW' && !cond.includes('NEW')) return false
        if (selectedCondition === 'USED' && !cond.includes('USED') && !cond.includes('PRE-OWNED')) return false
        if (selectedCondition === 'REFURBISHED' && !cond.includes('REFURBISHED')) return false
      }

      // 4. Category Filter
      if (selectedCategory && part.category?.toLowerCase() !== selectedCategory.toLowerCase()) {
        return false
      }

      // 5. Vehicle Brand
      if (selectedBrand) {
        const specs = (part.specifications as any) || {}
        const vb = part.vehicleBrand || specs.vehicleBrand || part.brand || ''
        const comp = Array.isArray(part.compatibility) ? part.compatibility.join(' ') : ''
        if (!vb.toLowerCase().includes(selectedBrand.toLowerCase()) && !comp.toLowerCase().includes(selectedBrand.toLowerCase())) {
          return false
        }
      }

      // 6. Vehicle Model
      if (selectedModel) {
        const specs = (part.specifications as any) || {}
        const vm = part.vehicleModel || specs.vehicleModel || ''
        const comp = Array.isArray(part.compatibility) ? part.compatibility.join(' ') : ''
        if (!vm.toLowerCase().includes(selectedModel.toLowerCase()) && !comp.toLowerCase().includes(selectedModel.toLowerCase())) {
          return false
        }
      }

      // 7. Availability Filter
      if (selectedAvailability !== 'ALL') {
        const avail = part.availability || (part.stockCount > 0 ? 'IN_STOCK' : 'ENQUIRY_REQUIRED')
        if (selectedAvailability === 'IN_STOCK' && avail !== 'IN_STOCK') return false
        if (selectedAvailability === 'ENQUIRY_REQUIRED' && avail !== 'ENQUIRY_REQUIRED') return false
      }

      // 8. OEM Only
      if (oemOnly && !part.isOEM) return false

      // 9. Price Filter
      const priceNum = typeof part.price === 'number' ? part.price : Number(String(part.price).replace(/[^0-9.]/g, '')) || 0
      if (minPrice && priceNum < Number(minPrice)) return false
      if (maxPrice && priceNum > Number(maxPrice)) return false

      return true
    })

    if (sortBy === 'featured') {
      return [...filtered].sort((a: any, b: any) => {
        const aFeat = Boolean(a.isFeatured || (a.specifications as any)?.isFeatured)
        const bFeat = Boolean(b.isFeatured || (b.specifications as any)?.isFeatured)
        return (bFeat ? 1 : 0) - (aFeat ? 1 : 0)
      })
    }
    if (sortBy === 'price-low') {
      return [...filtered].sort((a: any, b: any) => (Number(a.price) || 0) - (Number(b.price) || 0))
    }
    if (sortBy === 'price-high') {
      return [...filtered].sort((a: any, b: any) => (Number(b.price) || 0) - (Number(a.price) || 0))
    }

    return filtered
  }, [
    dbData,
    searchQuery,
    sortBy,
    activeTab,
    selectedCondition,
    selectedCategory,
    selectedBrand,
    selectedModel,
    selectedAvailability,
    oemOnly,
    minPrice,
    maxPrice,
  ])

  // Count active filters
  const activeFiltersCount = [
    activeTab !== 'ALL',
    selectedCondition !== 'ALL',
    Boolean(selectedCategory),
    Boolean(selectedBrand),
    Boolean(selectedModel),
    selectedAvailability !== 'ALL',
    oemOnly,
    Boolean(minPrice),
    Boolean(maxPrice),
  ].filter(Boolean).length

  const handleResetFilters = () => {
    setActiveTab('ALL')
    setSelectedCategory('')
    setSelectedCondition('ALL')
    setSelectedBrand('')
    setSelectedModel('')
    setSelectedAvailability('ALL')
    setOemOnly(false)
    setMinPrice('')
    setMaxPrice('')
    setSearchQuery('')
  }

  // Handle Find My Part Submit
  const handleApplyFinder = () => {
    setSelectedBrand(stepBrand)
    setSelectedModel(stepModel)
    setSelectedCategory(stepCategory)
    setIsFinderOpen(false)
    toast.success(`Filtered for ${stepBrand} ${stepModel} • ${stepCategory}`)
  }

  // Handle Request a Part Submit
  const handleSubmitRequest = (e: React.FormEvent) => {
    e.preventDefault()
    if (!requestData.partName || !requestData.phone) {
      toast.error('Please provide the Part Name and Contact Phone.')
      return
    }
    setIsSubmittingRequest(true)
    setTimeout(() => {
      setIsSubmittingRequest(false)
      setIsRequestModalOpen(false)
      toast.success('Your part request has been submitted to verified dealers & specialists! You will receive a quote shortly.')
      setRequestData({
        vehicleBrand: 'Maruti Suzuki',
        vehicleModel: 'Swift',
        year: '2019',
        partName: '',
        partNumber: '',
        phone: '',
        notes: '',
      })
    }, 700)
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-6 py-6">
      {/* ── Breadcrumb & Top Bar ─────────────────────────────────────────── */}
      <MarketplaceBreadcrumb items={[{ label: 'Car Spare Parts Catalog' }]} />

      {/* ── Client Navigation Bar (Matching Image 4) ────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/80 pb-4">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap items-center gap-1.5 p-1 rounded-xl bg-card border border-border/80">
          <button
            type="button"
            onClick={() => setActiveTab('ALL')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors ${
              activeTab === 'ALL'
                ? 'bg-primary text-white shadow-xs'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            All Auto Parts
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('NEW')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 ${
              activeTab === 'NEW'
                ? 'bg-primary text-white shadow-xs'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <span>New Parts</span>
            <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1 rounded font-semibold">OEM</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('USED')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 ${
              activeTab === 'USED'
                ? 'bg-primary text-white shadow-xs'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <span>Used Parts</span>
            <span className="text-[10px] bg-amber-500/20 text-amber-400 px-1 rounded font-semibold">Tested</span>
          </button>
        </div>

        {/* Action Buttons: Find My Part + Request Part */}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsFinderOpen(!isFinderOpen)}
            className="text-xs font-bold gap-1.5 border-primary/40 text-primary hover:bg-primary/10"
          >
            <Sparkles className="size-3.5 text-primary" />
            <span>Find My Part</span>
          </Button>

          <Button
            type="button"
            size="sm"
            onClick={() => setIsRequestModalOpen(true)}
            className="text-xs font-bold gap-1.5 bg-primary text-white hover:bg-primary/90 shadow-xs"
          >
            <PhoneCall className="size-3.5" />
            <span>Request a Part</span>
          </Button>
        </div>
      </div>

      {/* ── "Find My Part" Interactive Stepper Widget (Image 4) ──────────── */}
      {isFinderOpen && (
        <div className="rounded-2xl border border-primary/40 bg-gradient-to-br from-card via-card to-primary/5 p-5 shadow-lg space-y-4">
          <div className="flex items-center justify-between border-b border-border/60 pb-3">
            <div className="flex items-center gap-2 font-display text-base font-bold text-foreground">
              <Sparkles className="size-4 text-primary" />
              <span>Find My Part — 5-Step Precision Vehicle Match</span>
            </div>
            <button
              onClick={() => setIsFinderOpen(false)}
              className="text-xs text-muted-foreground hover:text-foreground font-semibold"
            >
              Close
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
            {/* Step 1: Vehicle Brand */}
            <div className="space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-primary">1. Brand</span>
              <select
                value={stepBrand}
                onChange={(e) => {
                  const b = e.target.value
                  setStepBrand(b)
                  const found = POPULAR_VEHICLE_BRANDS.find((brand) => brand.brand === b)
                  if (found) setStepModel(found.models[0])
                }}
                className="w-full h-9 rounded-lg border border-border bg-background px-2.5 text-xs font-semibold text-foreground"
              >
                {POPULAR_VEHICLE_BRANDS.map((b) => (
                  <option key={b.brand} value={b.brand}>
                    {b.brand}
                  </option>
                ))}
              </select>
            </div>

            {/* Step 2: Model */}
            <div className="space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-primary">2. Model</span>
              <select
                value={stepModel}
                onChange={(e) => setStepModel(e.target.value)}
                className="w-full h-9 rounded-lg border border-border bg-background px-2.5 text-xs font-semibold text-foreground"
              >
                {stepBrandModels.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            {/* Step 3: Manufacturing Year */}
            <div className="space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-primary">3. Year</span>
              <select
                value={stepYear}
                onChange={(e) => setStepYear(e.target.value)}
                className="w-full h-9 rounded-lg border border-border bg-background px-2.5 text-xs font-semibold text-foreground"
              >
                {POPULAR_YEARS.slice(0, 15).map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            {/* Step 4: Engine Variant */}
            <div className="space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-primary">4. Engine</span>
              <select
                value={stepVariant}
                onChange={(e) => setStepVariant(e.target.value)}
                className="w-full h-9 rounded-lg border border-border bg-background px-2.5 text-xs font-semibold text-foreground truncate"
              >
                {ENGINE_VARIANTS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>

            {/* Step 5: Part Category */}
            <div className="space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-primary">5. Part</span>
              <select
                value={stepCategory}
                onChange={(e) => setStepCategory(e.target.value)}
                className="w-full h-9 rounded-lg border border-border bg-background px-2.5 text-xs font-semibold text-foreground truncate"
              >
                {PARTS_CATEGORIES_DATA.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              onClick={handleApplyFinder}
              className="bg-primary text-white font-bold text-xs gap-1.5"
            >
              <Search className="size-3.5" />
              <span>Show Matching Parts for {stepBrand} {stepModel}</span>
            </Button>
          </div>
        </div>
      )}

      {/* ── Listing Header (Search, Layout & Sort) ───────────────────────── */}
      <ListingHeader
        title={
          activeTab === 'NEW'
            ? 'New & OEM Auto Parts'
            : activeTab === 'USED'
            ? 'Used & Pre-Owned Tested Auto Parts'
            : 'All Automotive Spare Parts'
        }
        count={partsList.length}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        layout={layout}
        onLayoutChange={setLayout}
        sortBy={sortBy}
        onSortChange={setSortBy}
        onToggleMobileFilter={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
      />

      {/* ── Main Layout: Sidebar Filters + Parts Grid ────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start">
        {/* Desktop Parts Filters Sidebar */}
        <aside className="hidden lg:block lg:col-span-1 space-y-5 rounded-2xl border border-border/80 bg-card p-5 sticky top-24 shadow-xs">
          <div className="flex items-center justify-between border-b border-border/80 pb-3">
            <div className="flex items-center gap-2 font-display text-base font-bold text-foreground">
              <Filter className="h-4 w-4 text-primary" /> Filter Parts Catalog
            </div>
            {activeFiltersCount > 0 && (
              <button
                onClick={handleResetFilters}
                className="flex items-center gap-1 text-[11px] font-bold text-muted-foreground hover:text-primary transition-colors"
              >
                <RotateCcw className="h-3 w-3" /> Reset ({activeFiltersCount})
              </button>
            )}
          </div>

          {/* 1. Condition Filter */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Condition
            </label>
            <select
              value={selectedCondition}
              onChange={(e) => setSelectedCondition(e.target.value)}
              className="w-full h-9 rounded-xl bg-background border border-border px-3 text-xs font-semibold text-foreground focus:border-primary focus:outline-none"
            >
              {PART_CONDITIONS.map((cond) => (
                <option key={cond.value} value={cond.value}>
                  {cond.label}
                </option>
              ))}
            </select>
          </div>

          {/* 2. Part Category (11 Categories) */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Part Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full h-9 rounded-xl bg-background border border-border px-3 text-xs font-semibold text-foreground focus:border-primary focus:outline-none"
            >
              <option value="">All 11 Categories</option>
              {PARTS_CATEGORIES_DATA.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* 3. Vehicle Brand */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Vehicle Brand
            </label>
            <select
              value={selectedBrand}
              onChange={(e) => {
                setSelectedBrand(e.target.value)
                setSelectedModel('')
              }}
              className="w-full h-9 rounded-xl bg-background border border-border px-3 text-xs font-semibold text-foreground focus:border-primary focus:outline-none"
            >
              <option value="">All Brands (Maruti, Hyundai, etc.)</option>
              {POPULAR_VEHICLE_BRANDS.map((b) => (
                <option key={b.brand} value={b.brand}>
                  {b.brand}
                </option>
              ))}
            </select>
          </div>

          {/* 4. Vehicle Model */}
          {selectedBrand && brandModels.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Model for {selectedBrand}
              </label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full h-9 rounded-xl bg-background border border-border px-3 text-xs font-semibold text-foreground focus:border-primary focus:outline-none"
              >
                <option value="">All Models</option>
                {brandModels.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* 5. Availability Status */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Availability
            </label>
            <select
              value={selectedAvailability}
              onChange={(e) => setSelectedAvailability(e.target.value)}
              className="w-full h-9 rounded-xl bg-background border border-border px-3 text-xs font-semibold text-foreground focus:border-primary focus:outline-none"
            >
              <option value="ALL">All Stock Types</option>
              <option value="IN_STOCK">In Stock (Instant Buy)</option>
              <option value="ENQUIRY_REQUIRED">Enquiry Required</option>
            </select>
          </div>

          {/* 6. Price Range */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Price Range (₹)
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                placeholder="₹ Min"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                className="h-8 text-xs px-2.5 rounded-lg"
              />
              <span className="text-muted-foreground text-xs">-</span>
              <Input
                type="number"
                placeholder="₹ Max"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                className="h-8 text-xs px-2.5 rounded-lg"
              />
            </div>
          </div>

          {/* 7. OEM Only Checkbox */}
          <div className="pt-2 border-t border-border/80">
            <label className="flex items-center gap-2.5 text-xs font-semibold text-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={oemOnly}
                onChange={(e) => setOemOnly(e.target.checked)}
                className="h-4 w-4 rounded border-border bg-background text-primary focus:ring-primary"
              />
              <span className="flex items-center gap-1">
                <ShieldCheck className="size-3.5 text-primary" /> Genuine OEM Parts Only
              </span>
            </label>
          </div>
        </aside>

        {/* Mobile Filter Drawer (when triggered) */}
        {isMobileFilterOpen && (
          <aside className="lg:hidden col-span-1 space-y-4 rounded-2xl border border-border bg-card p-5 shadow-xs">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center gap-2 font-display text-base font-bold text-foreground">
                <Filter className="h-4 w-4 text-primary" /> Filter Parts Catalog
              </div>
              <button
                onClick={handleResetFilters}
                className="text-xs font-bold text-muted-foreground hover:text-primary transition-colors"
              >
                Reset All
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Condition</label>
                <select
                  value={selectedCondition}
                  onChange={(e) => setSelectedCondition(e.target.value)}
                  className="w-full h-8 rounded-lg bg-background border border-border px-2 text-xs font-semibold"
                >
                  {PART_CONDITIONS.map((cond) => (
                    <option key={cond.value} value={cond.value}>
                      {cond.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full h-8 rounded-lg bg-background border border-border px-2 text-xs font-semibold"
                >
                  <option value="">All Categories</option>
                  {PARTS_CATEGORIES_DATA.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </aside>
        )}

        {/* Parts Grid / List */}
        <div className="lg:col-span-3">
          {isLoading ? (
            <div className="h-64 flex flex-col items-center justify-center text-muted-foreground space-y-2">
              <Loader2 className="size-8 animate-spin text-primary" />
              <p className="text-xs font-medium">Fetching verified spare parts catalog...</p>
            </div>
          ) : partsList.length > 0 ? (
            <div className={layout === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-6'}>
              {partsList.map((part) => (
                <PartCard
                  key={part.id}
                  part={part}
                  layout={layout}
                  onItemAdded={() => setIsCartOpen(true)}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              title="No Matching Auto Parts Found"
              description="No components match your selected filter criteria. Try broadening your search or submit a custom part sourcing request."
            />
          )}
        </div>
      </div>

      {/* ── "Request a Part" Modal (Client Image 4) ──────────────────────── */}
      <Dialog open={isRequestModalOpen} onOpenChange={setIsRequestModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold flex items-center gap-2">
              <PhoneCall className="size-5 text-primary" />
              <span>Request a Part / Custom Sourcing</span>
            </DialogTitle>
          </DialogHeader>

          <p className="text-xs text-muted-foreground">
            Can't find the exact spare part for your car or model? Submit your vehicle details and our verified dealer network will source it for you.
          </p>

          <form onSubmit={handleSubmitRequest} className="space-y-3.5 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-foreground">
                  Vehicle Brand *
                </label>
                <select
                  className="w-full h-8 rounded-lg border border-input bg-background px-2.5 text-xs font-medium"
                  value={requestData.vehicleBrand}
                  onChange={(e) => {
                    const b = e.target.value
                    const found = POPULAR_VEHICLE_BRANDS.find((brand) => brand.brand === b)
                    setRequestData({
                      ...requestData,
                      vehicleBrand: b,
                      vehicleModel: found ? found.models[0] : 'Standard Model',
                    })
                  }}
                >
                  {POPULAR_VEHICLE_BRANDS.map((b) => (
                    <option key={b.brand} value={b.brand}>
                      {b.brand}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-foreground">
                  Model &amp; Year *
                </label>
                <Input
                  required
                  placeholder="e.g. Swift 2019"
                  value={`${requestData.vehicleModel}`}
                  onChange={(e) => setRequestData({ ...requestData, vehicleModel: e.target.value })}
                  className="h-8 text-xs"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold uppercase tracking-wider text-foreground">
                Part Name / Component Needed *
              </label>
              <Input
                required
                placeholder="e.g. Alternator, Front Brake Caliper, Radiator"
                value={requestData.partName}
                onChange={(e) => setRequestData({ ...requestData, partName: e.target.value })}
                className="h-8 text-xs"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-foreground">
                  OEM / Part # (Optional)
                </label>
                <Input
                  placeholder="e.g. 55810-77M00"
                  value={requestData.partNumber}
                  onChange={(e) => setRequestData({ ...requestData, partNumber: e.target.value })}
                  className="h-8 text-xs"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-foreground">
                  Contact Phone / WhatsApp *
                </label>
                <Input
                  required
                  placeholder="+91 98765 43210"
                  value={requestData.phone}
                  onChange={(e) => setRequestData({ ...requestData, phone: e.target.value })}
                  className="h-8 text-xs"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold uppercase tracking-wider text-foreground">
                Additional Notes &amp; Fitment Details
              </label>
              <textarea
                className="w-full min-h-[60px] rounded-lg border border-input bg-background p-2 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Specify engine type (Petrol/Diesel), VIN number if available, or preferred condition (New/Used)..."
                value={requestData.notes}
                onChange={(e) => setRequestData({ ...requestData, notes: e.target.value })}
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setIsRequestModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={isSubmittingRequest} className="bg-primary text-white font-bold gap-1.5">
                {isSubmittingRequest ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
                <span>Submit Sourcing Request</span>
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cart Drawer */}
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  )
}
